import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useToast } from "~components/shared/useToast.tsx";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import type { Team } from "~generated";
import { TeamsService } from "~generated";
import { allEmployeeKeys, employeeKeys } from "./employees";
import type { QueryOpts } from "./helpers.ts";
import { managerKeys } from "./managerKeys.ts";
import { teamAssignmentKeys } from "./teamAssignmentKeys.ts";
import { tenantKeys } from "./tenants";
import { toolKeys } from "./toolKeys";

const teamKeys = {
  base: (tenantId: string) =>
    [...tenantKeys.detail(tenantId), "teams", "v1"] as const,
  list: (tenantId: string, includeDeleted?: boolean) =>
    [...teamKeys.base(tenantId), "list", includeDeleted] as const,
};

async function getTeams(tenantId: string, includeDeleted?: boolean) {
  const teams = await TeamsService.getTeams(tenantId, includeDeleted);
  if (!teams?.teams) {
    return [];
  }

  return teams.teams.sort((a, b) => a.name.localeCompare(b.name));
}

export function useTeams(
  {
    includeDeleted,
    options,
  }: {
    includeDeleted?: boolean;
    options?: QueryOpts<Team[], ReturnType<typeof teamKeys.list>>;
  } = { includeDeleted: false, options: {} },
) {
  const currentTenant = useCurrentTenant();
  return useQuery({
    ...options,
    queryKey: teamKeys.list(currentTenant.id, includeDeleted || false),
    queryFn: async () => getTeams(currentTenant.id, includeDeleted),
  });
}

export function useCreateTeam() {
  const { showSuccessToast } = useToast();
  const t = useTranslation();
  const currentTenant = useCurrentTenant();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (team: Team) =>
      TeamsService.createTeam(currentTenant.id, team),
    onSuccess: async () => {
      showSuccessToast(t.addTeamSuccess);

      await invalidateQueriesForTeamUpdates({
        queryClient,
        tenantId: currentTenant.id,
      });
    },
  });
  return mutation;
}

export function useUpdateTeam() {
  const { showSuccessToast } = useToast();
  const t = useTranslation();
  const currentTenant = useCurrentTenant();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (team: Team) =>
      TeamsService.updateTeam(currentTenant.id, team.id as string, team),

    onSuccess: async () => {
      showSuccessToast(t.editSuccess);

      await invalidateQueriesForTeamUpdates({
        queryClient,
        tenantId: currentTenant.id,
      });
    },
  });
  return mutation;
}

export function useDeleteTeam() {
  const { showSuccessToast } = useToast();
  const t = useTranslation();
  const currentTenant = useCurrentTenant();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (teamId: string) =>
      TeamsService.deleteTeam(currentTenant.id, teamId),

    onSuccess: async () => {
      showSuccessToast(t.deleteTeamSuccess);

      await invalidateQueriesForTeamUpdates({
        queryClient,
        tenantId: currentTenant.id,
      });
    },
  });
  return mutation;
}

async function invalidateQueriesForTeamUpdates({
  queryClient,
  tenantId,
}: {
  queryClient: ReturnType<typeof useQueryClient>;
  tenantId: string;
}) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: teamKeys.base(tenantId) }),
    queryClient.invalidateQueries({ queryKey: allEmployeeKeys.base() }),
    queryClient.invalidateQueries({ queryKey: employeeKeys.list(tenantId) }),
    queryClient.invalidateQueries({ queryKey: toolKeys.list() }),
    queryClient.invalidateQueries({ queryKey: managerKeys.list(tenantId) }),
    queryClient.invalidateQueries({
      queryKey: teamAssignmentKeys.base(tenantId),
    }),
  ]);
}
